'use client';

import { useState, type ReactNode } from 'react';
import { Check, UserRound, X } from 'lucide-react';
import {
  ResponsiveSheet,
  SheetPage,
  useResponsiveSheet,
} from '@church/nextjs-ui/components/ResponsiveSheet';
import { sectionedChoices } from '@/lib/relationships';
import { formatPhone } from '@/lib/format';
import {
  GENDERS,
  HOUSEHOLD_POSITIONS,
  MARITAL_STATUSES,
  SHIRT_GROUPS,
  SHIRT_SIZES,
  positionFor,
  type Lookup,
} from '@/lib/lookups';
import { Avatar } from './Avatar';
import { errorText, type FormerMemberLite } from '@/lib/api';
import { guestReady, type GuestDraft } from './SignUp';
import { blankParent, needsParent, type ParentDraft } from '@/lib/guests';

/**
 * ADDING SOMEBODY THE CHURCH DOES NOT KNOW ABOUT YET
 * ==================================================
 *
 * A sheet rather than four fields grown out of a list row, for the reason
 * sheets exist: this is a short interview and interviews have pages. One
 * question at a time is also the only shape that leaves room for the questions
 * still to come — the match confirmation ("we think we already have her, is
 * this the same person?"), the gender follow-up where a chip did not imply
 * one, a household position. Each of those is a page, not another box on a
 * column that is 22rem wide.
 *
 * WHAT IT ASKS, AND WHY THAT ORDER.
 *
 * 1. Their name, because every other question reads better with it in.
 * 2. Who they are to you — the gendered chips, so "Mom" answers the
 *    relationship and the gender in one tap. This is saved to MP whether or
 *    not the registration is ever finished, because a fact about two people
 *    outlives one form.
 * 3. Whether they join the household.
 *
 *    YES adds them. Not a request, not a match, not a queue: a household is
 *    the one set of records a person plainly owns, and somebody adding their
 *    own newborn or the stepson who just moved in should not be told to wait
 *    for anyone's approval. The contact, the household place and the
 *    relationship are all written on the spot, and a duplicate that comes of
 *    it is a thing staff merge later — which they are already doing, 1,503
 *    times over, with tooling built for exactly that. Confirming is for
 *    claims on records that are not yours.
 *
 *    It is also what makes MP need a birthday, because a household places
 *    people by age, and a nine-year-old has no email and no reason to be
 *    asked for one.
 *
 *    NO is not a dead end either: the relationship is written anyway, and
 *    that is how somebody becomes a person on next year's list instead of a
 *    stranger retyped every autumn.
 *
 * REMOVING SOMEBODY IS NOT THE MIRROR OF THIS, and nothing here should be
 * read as permission to build it. Adding is additive and its worst case is a
 * duplicate staff can merge; removing is somebody being taken off records
 * that are partly theirs, and the reasons people reach for it — a death, a
 * divorce, a child moving out, an adult who wants their own household — are
 * each a different operation with different consequences for giving
 * statements, check-in, and who can still see whom. It needs its own design.
 * Until then this sheet only ever adds.
 *
 * WHAT IT NEVER DOES is tell the viewer about anybody. Everything on these
 * pages is something they typed. If a match turns up on the way out, it is
 * confirmed by the person matched, not disclosed to the person asking.
 *
 * The one exception is the viewer's OWN former household: someone archived out
 * of it (a Guest Child who stopped coming) is somebody they lived with, not a
 * stranger. When the name typed matches one, the sheet offers to add that
 * record back rather than create a second one.
 */

const FIRST_LABEL = 'First name';

export interface AddPersonProps {
  open: boolean;
  draft: GuestDraft | null;
  onChange: (patch: Partial<GuestDraft>) => void;
  /** Write them down. Resolves when MP has said what became of them. */
  onSave: (draft: GuestDraft) => Promise<void>;
  /** Close, keeping what was filled in. */
  onClose: () => void;
  /** Close and drop the draft entirely. */
  onRemove: () => void;
  /** Archived former members of the viewer's household. */
  formerMembers?: FormerMemberLite[];
  /** Add one back instead of creating this draft. */
  onBringBack?: (contactId: number) => Promise<void>;
}

/** A former member whose name matches what was typed, loosely. */
function matchFormer(
  formerMembers: FormerMemberLite[] | undefined,
  first: string,
  last: string
): FormerMemberLite | null {
  const f = first.trim().toLowerCase();
  const l = last.trim().toLowerCase();
  if (!formerMembers?.length || f.length < 2) return null;
  return (
    formerMembers.find((m) => {
      const mf = m.firstName.trim().toLowerCase();
      if (mf.length < 2 || !(mf.startsWith(f) || f.startsWith(mf))) return false;
      return !l || m.name.toLowerCase().endsWith(` ${l}`);
    }) ?? null
  );
}

function FormerMatch({
  match,
  onBringBack,
}: {
  match: FormerMemberLite;
  onBringBack: (contactId: number) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  return (
    <div className="bg-mist mx-5 mt-1 mb-2 flex items-center gap-3 rounded-2xl px-4 py-3">
      <Avatar name={match.name} image={match.imageUrl} size={32} />
      <p className="text-navy min-w-0 flex-1 text-sm">
        <span className="font-bold">{match.name}</span> used to be in your household.
        {failed ? (
          <span className="block text-xs text-red-600">That did not work. Try again.</span>
        ) : null}
      </p>
      <button
        type="button"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          setFailed(false);
          onBringBack(match.contactId)
            .catch(() => setFailed(true))
            .finally(() => setBusy(false));
        }}
        className="text-teal shrink-0 text-sm font-extrabold hover:underline disabled:opacity-50"
      >
        {busy ? 'Adding…' : 'Add back'}
      </button>
    </div>
  );
}

export function AddPerson({
  open,
  draft,
  onChange,
  onSave,
  onClose,
  onRemove,
  formerMembers,
  onBringBack,
}: AddPersonProps) {
  const former = matchFormer(formerMembers, draft?.firstName ?? '', draft?.lastName ?? '');
  const first = draft?.firstName.trim() ?? '';
  const named = Boolean(first && draft?.lastName.trim());
  const them = first || 'them';

  return (
    <ResponsiveSheet
      open={open && Boolean(draft)}
      onClose={onClose}
      maxWidth="max-w-lg"
      panelClassName="bg-ground text-ink md:rounded-3xl"
      noPanelPadding
      hideCloseButton
      header={
        <div className="ed flex items-start gap-3 px-5 pt-5 pb-3">
          <div className="flex items-center gap-3">
            {named ? (
              <Avatar name={`${first} ${draft?.lastName ?? ''}`.trim()} size={40} />
            ) : (
              <span className="bg-mist text-slate flex h-10 w-10 items-center justify-center rounded-full">
                <UserRound className="h-5 w-5" />
              </span>
            )}
            <div className="min-w-0">
              <p className="kicker">Someone else</p>
              <p className="text-navy truncate text-lg font-extrabold">
                {named ? `${first} ${draft?.lastName}` : 'Add a person'}
              </p>
            </div>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="bg-mist hover:bg-line text-navy ml-auto flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      }
    >
      <SheetPage name="main" title="Name">
        {former && onBringBack ? <FormerMatch match={former} onBringBack={onBringBack} /> : null}
        <NamePage draft={draft} onChange={onChange} onRemove={onRemove} />
      </SheetPage>

      <SheetPage name="relation" title={`Who ${them} is`}>
        <RelationPage draft={draft} onChange={onChange} them={them} />
      </SheetPage>

      <SheetPage name="where" title="Household">
        <WherePage draft={draft} onChange={onChange} them={them} onDone={onSave} />
      </SheetPage>

      <SheetPage name="more" title="Details">
        <MorePage draft={draft} onChange={onChange} them={them} onDone={onSave} />
      </SheetPage>
    </ResponsiveSheet>
  );
}

function NamePage({
  draft,
  onChange,
  onRemove,
}: {
  draft: GuestDraft | null;
  onChange: (patch: Partial<GuestDraft>) => void;
  onRemove: () => void;
}) {
  const { navigate } = useResponsiveSheet();
  const ready = Boolean(draft?.firstName.trim() && draft?.lastName.trim());

  return (
    <div className="ed space-y-3 px-5 pb-8">
      <p className="text-slate text-sm">
        Who are you adding?
        <Required />
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <input
          className="field"
          placeholder={FIRST_LABEL}
          value={draft?.firstName ?? ''}
          onChange={(e) => onChange({ firstName: e.target.value })}
          onKeyDown={(e) => e.key === 'Enter' && ready && navigate('relation')}
          autoComplete="off"
          // The one field worth focusing: everything else follows from it.
          autoFocus
        />
        <input
          className="field"
          placeholder="Last name"
          value={draft?.lastName ?? ''}
          onChange={(e) => onChange({ lastName: e.target.value })}
          onKeyDown={(e) => e.key === 'Enter' && ready && navigate('relation')}
          autoComplete="off"
        />
      </div>
      <button
        type="button"
        disabled={!ready}
        onClick={() => navigate('relation')}
        className="btn btn-primary w-full disabled:opacity-40"
      >
        Continue
      </button>
      <button
        type="button"
        onClick={onRemove}
        className="text-slate w-full py-2 text-sm font-semibold"
      >
        Never mind
      </button>
    </div>
  );
}

function RelationPage({
  draft,
  onChange,
  them,
}: {
  draft: GuestDraft | null;
  onChange: (patch: Partial<GuestDraft>) => void;
  them: string;
}) {
  const { navigate } = useResponsiveSheet();

  return (
    <div className="ed space-y-3 px-5 pb-8">
      {/*
        The question and the way past it, on one line. Skipping belongs beside
        the question rather than under the chips: at the bottom it read as the
        last option in a list of options, which is the thing it is not.
      */}
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-slate text-sm">Who is {them} to you?</p>
        {/*
          It replaces a "Someone else" chip that sat at the end of the list and
          wrote no relationship — it looked like an answer, recorded nothing,
          and left somebody believing they had told us something. The outcome is
          the same; the difference is that this one admits it.

          Quiet rather than a button, because the answer is worth having: every
          relationship makes the household easier to get right next time. Worth
          asking for, not worth blocking on.
        */}
        <button
          type="button"
          onClick={() => {
            onChange({
              relationChoice: null,
              relationAsked: true,
              relationshipId: null,
              gender: null,
            });
            navigate('where');
          }}
          className="text-slate hover:text-navy shrink-0 text-xs font-semibold underline underline-offset-2"
        >
          Skip this
        </button>
      </div>

      {/*
        Sectioned in MP's own words, because twenty-three chips in one wall is
        a wall. Primary family comes first and covers almost every answer, so
        most people never read past the first eight.
      */}
      <div className="space-y-3">
        {sectionedChoices().map((section) => (
          <div key={section.name} className="space-y-1.5">
            <p className="text-slate/70 text-[10px] font-semibold tracking-wider uppercase">
              {section.name}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {section.choices.map((c) => {
                const on = draft?.relationChoice === c.label;
                return (
                  <button
                    key={c.label}
                    type="button"
                    aria-pressed={on}
                    onClick={() => {
                      onChange({
                        relationChoice: c.label,
                        relationAsked: true,
                        relationshipId: c.relationshipId,
                        gender: c.gender,
                      });
                      // One tap is the whole answer, so nothing to confirm.
                      navigate('where');
                    }}
                    className={`chip ${on ? '!bg-teal !border-teal !text-white' : ''}`}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function WherePage({
  draft,
  onChange,
  them,
  onDone,
}: {
  draft: GuestDraft | null;
  onChange: (patch: Partial<GuestDraft>) => void;
  them: string;
  onDone: (draft: GuestDraft) => Promise<void>;
}) {
  const { navigate } = useResponsiveSheet();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Null until they answer: "no" is a real answer and must not be the default
  // simply because a checkbox starts unticked.
  const answered = draft?.whereAnswered ?? false;
  const inHousehold = draft?.addToHousehold ?? false;
  const ready = draft ? guestReady(draft) : false;

  return (
    <div className="ed space-y-3 px-5 pb-8">
      <p className="text-slate text-sm">
        Does {them} live with you?
        <Required />
      </p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <Choice
          on={answered && inHousehold}
          title="Yes, add to my household"
          onClick={() => onChange({ whereAnswered: true, addToHousehold: true })}
        />
        <Choice
          on={answered && !inHousehold}
          title="No, they live elsewhere"
          onClick={() => onChange({ whereAnswered: true, addToHousehold: false })}
        />
      </div>

      {answered && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {/* The birthday is what MP needs to place somebody in a household;
              an email is worth having from anyone old enough to read one, and
              asking for it here rather than three fields deeper is the whole
              difference between getting it and not. */}
          {/* Living elsewhere, either one finds them: a student brought by a
              leader often has a birthday and no email. */}
          <Field label="Date of birth" required={inHousehold || !draft?.email?.trim()}>
            <input
              className="field"
              type="date"
              value={draft?.dateOfBirth ?? ''}
              onChange={(e) => onChange({ dateOfBirth: e.target.value })}
            />
          </Field>
          <Field label="Email" required={!inHousehold && !draft?.dateOfBirth}>
            <input
              className="field"
              type="email"
              value={draft?.email ?? ''}
              onChange={(e) => onChange({ email: e.target.value })}
              autoComplete="off"
            />
          </Field>
          <Field label="Mobile phone">
            <input
              className="field"
              type="tel"
              value={draft?.phone ?? ''}
              onChange={(e) => onChange({ phone: formatPhone(e.target.value) })}
              autoComplete="off"
            />
          </Field>
        </div>
      )}

      {/* A minor living elsewhere goes into their parent's household, with the
          parent as their emergency contact, instead of onto a record of their
          own. "I don't know" puts them in yours as a guest. */}
      {draft && answered && needsParent(draft) && (
        <ParentFields
          parent={draft.parent ?? blankParent()}
          onChange={(patch) =>
            onChange({ parent: { ...(draft.parent ?? blankParent()), ...patch } })
          }
        />
      )}

      {inHousehold ? (
        <button
          type="button"
          disabled={!ready}
          onClick={() => navigate('more')}
          className="btn btn-primary w-full disabled:opacity-40"
        >
          Continue
        </button>
      ) : (
        <>
          <button
            type="button"
            disabled={!ready || saving}
            onClick={async () => {
              if (!draft) return;
              setError(null);
              setSaving(true);
              try {
                await onDone(draft);
              } catch (err) {
                setError(errorText(err, 'We could not add them.'));
              } finally {
                setSaving(false);
              }
            }}
            className="btn btn-primary w-full disabled:opacity-40"
          >
            {saving ? 'Adding…' : `Add ${them}`}
          </button>
          {error && <p className="text-rose text-xs font-semibold">{error}</p>}
        </>
      )}
    </div>
  );
}

/**
 * EVERYTHING ELSE A PROFILE HOLDS, AND ALL OF IT OPTIONAL.
 *
 * Only on the household branch, and that distinction is the whole point:
 * these are fields about a person whose records the viewer plainly owns, so
 * asking them is filling in our own blanks rather than asserting things about
 * a stranger. Nothing here gates the button — the page opens with a person who
 * can already be added, and every field is a bonus.
 *
 * Two are answered before the page loads. GENDER comes from the chip, because
 * "Daughter" said it, and it is shown rather than hidden so anybody we guessed
 * wrong about can correct us. HOUSEHOLD POSITION is worked out from the
 * relationship and the birthday, because "household position" is staff
 * vocabulary and no parent should have to learn it to finish a form.
 */
function MorePage({
  draft,
  onChange,
  them,
  onDone,
}: {
  draft: GuestDraft | null;
  onChange: (patch: Partial<GuestDraft>) => void;
  them: string;
  onDone: (draft: GuestDraft) => Promise<void>;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!draft) return null;
  const position = draft.positionId ?? positionFor(draft.relationshipId, draft.dateOfBirth);
  const genderId = draft.gender === 'male' ? 1 : draft.gender === 'female' ? 2 : null;

  return (
    <div className="ed space-y-3 px-5 pb-8">
      <p className="text-slate text-sm">Anything else about {them}? All optional.</p>

      {/* Two up wherever there is room. Every field on this page is short, and
          a single column of them turns nine small answers into a scroll —
          which reads as a long form rather than the optional extras they are.
          One column below 640px, where there is no room to pair anything. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Goes by">
          <input
            className="field"
            placeholder={draft.firstName.trim()}
            value={draft.nickname}
            onChange={(e) => onChange({ nickname: e.target.value })}
            autoComplete="off"
          />
        </Field>

        <Field label="Middle name">
          <input
            className="field"
            value={draft.middleName}
            onChange={(e) => onChange({ middleName: e.target.value })}
            autoComplete="off"
          />
        </Field>

        <Field label="Gender">
          <Choices
            options={GENDERS}
            value={genderId}
            onChange={(id) => onChange({ gender: id === 1 ? 'male' : id === 2 ? 'female' : null })}
          />
        </Field>

        <Field label="In the household">
          <Select
            options={HOUSEHOLD_POSITIONS}
            value={position}
            onChange={(id) => onChange({ positionId: id })}
          />
        </Field>

        <Field label="Marital status">
          <Select
            options={MARITAL_STATUSES}
            value={draft.maritalStatusId}
            onChange={(id) => onChange({ maritalStatusId: id })}
          />
        </Field>

        <Field label="Shirt size">
          <select
            className="field"
            value={draft.shirtSizeId ?? ''}
            onChange={(e) =>
              onChange({ shirtSizeId: e.target.value ? Number(e.target.value) : null })
            }
          >
            <option value="">Not set</option>
            {SHIRT_GROUPS.map((group) => (
              <optgroup key={group} label={group}>
                {SHIRT_SIZES.filter((z) => z.group === group)
                  .sort((a, b) => a.sort - b.sort)
                  .map((z) => (
                    <option key={z.id} value={z.id}>
                      {z.label}
                    </option>
                  ))}
              </optgroup>
            ))}
          </select>
        </Field>

        <Field label="Work phone">
          <input
            className="field"
            type="tel"
            value={draft.workPhone}
            onChange={(e) => onChange({ workPhone: formatPhone(e.target.value) })}
            autoComplete="off"
          />
        </Field>
      </div>

      <button
        type="button"
        disabled={saving}
        onClick={async () => {
          // The derived position is only a suggestion until it is saved with
          // everything else, so send it whether or not the select was touched.
          const finished = { ...draft, positionId: draft.positionId ?? position };
          if (draft.positionId == null && position != null) onChange({ positionId: position });
          setError(null);
          setSaving(true);
          try {
            await onDone(finished);
          } catch (err) {
            setError(errorText(err, 'We could not add them.'));
          } finally {
            setSaving(false);
          }
        }}
        className="btn btn-primary w-full disabled:opacity-40"
      >
        {saving ? 'Adding…' : `Add ${them}`}
      </button>
      {error && <p className="text-rose text-xs font-semibold">{error}</p>}
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-slate mb-1 block text-xs font-semibold">
        {label}
        {required && <Required />}
      </span>
      {children}
    </label>
  );
}

/** Two or three options are chips; more than that is a select. */
function Choices({
  options,
  value,
  onChange,
}: {
  options: Lookup[];
  value: number | null;
  onChange: (id: number | null) => void;
}) {
  return (
    <span className="flex flex-wrap gap-1.5">
      {options.map((o) => {
        const on = value === o.id;
        return (
          <button
            key={o.id}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(on ? null : o.id)}
            className={`chip ${on ? '!bg-teal !border-teal !text-white' : ''}`}
          >
            {o.label}
          </button>
        );
      })}
    </span>
  );
}

function Select({
  options,
  value,
  onChange,
}: {
  options: Lookup[];
  value: number | null;
  onChange: (id: number | null) => void;
}) {
  return (
    <select
      className="field"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
    >
      <option value="">Not set</option>
      {options.map((o) => (
        <option key={o.id} value={o.id}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

/**
 * A required mark, the same one MP's own forms use. Every question in this
 * sheet is required — there is no version of "add this person" that works
 * without knowing who they are — and saying so beats letting somebody find out
 * from a button that will not light.
 */
const PARENT_ROLES: { role: ParentDraft['role']; label: string }[] = [
  { role: 'mom', label: 'Mom' },
  { role: 'dad', label: 'Dad' },
  { role: 'guardian', label: 'Guardian' },
  { role: 'unknown', label: "I don't know" },
];

function ParentFields({
  parent,
  onChange,
}: {
  parent: ParentDraft;
  onChange: (patch: Partial<ParentDraft>) => void;
}) {
  const known = parent.role && parent.role !== 'unknown';
  return (
    <div className="space-y-3">
      <p className="text-slate text-sm">
        Their parent or guardian
        <Required />
      </p>
      <span className="flex flex-wrap gap-1.5">
        {PARENT_ROLES.map((r) => {
          const on = parent.role === r.role;
          return (
            <button
              key={r.label}
              type="button"
              aria-pressed={on}
              onClick={() => onChange({ role: on ? null : r.role })}
              className={`chip ${on ? '!bg-teal !border-teal !text-white' : ''}`}
            >
              {r.label}
            </button>
          );
        })}
      </span>
      {known && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="First name" required>
            <input
              className="field"
              value={parent.firstName}
              onChange={(e) => onChange({ firstName: e.target.value })}
              autoComplete="off"
            />
          </Field>
          <Field label="Last name" required>
            <input
              className="field"
              value={parent.lastName}
              onChange={(e) => onChange({ lastName: e.target.value })}
              autoComplete="off"
            />
          </Field>
          <Field label="Mobile phone" required>
            <input
              className="field"
              type="tel"
              value={parent.phone}
              onChange={(e) => onChange({ phone: formatPhone(e.target.value) })}
              autoComplete="off"
            />
          </Field>
          <Field label="Email">
            <input
              className="field"
              type="email"
              value={parent.email}
              onChange={(e) => onChange({ email: e.target.value })}
              autoComplete="off"
            />
          </Field>
        </div>
      )}
    </div>
  );
}

function Required() {
  return <span className="text-rose ml-0.5 font-bold">*</span>;
}

/**
 * One of two answers, drawn so the answer is obvious.
 *
 * It was a ring at first, and it drew nothing: `.ed .card` sets its own
 * box-shadow at two classes of specificity and Tailwind's ring IS a box-shadow
 * at one, so the selected card looked exactly like the unselected one. The dot
 * is the fix that does not depend on winning a cascade argument.
 */
function Choice({ on, title, onClick }: { on: boolean; title: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className={`card lift flex items-start gap-3 p-3.5 text-left ${
        on ? '!border-teal !bg-teal-soft' : ''
      }`}
    >
      <span
        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition ${
          on ? 'bg-teal border-teal text-white' : 'border-line bg-white'
        }`}
      >
        {on && <Check className="h-3 w-3" strokeWidth={3} />}
      </span>
      <span className="text-navy min-w-0 text-sm font-bold">{title}</span>
    </button>
  );
}
